import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ImageUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { environment } from '../../../environments/environment';
import { IDigitalProductBillingType } from '../../models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../models/digitalProduct.model';
import { DigitalProductService } from '../../services/digitalProduct.service';
import { digitalProductActions } from '../../store/digitalProduct.actions';
import { getCurrentDigitalProductSelector } from '../../store/digitalProduct.selectors';

@Component({
    selector: 'clp-details-digital-product',
    styleUrls: ['./digitalProductDetails.component.scss'],
    templateUrl: './digitalProductDetails.component.html',
    imports: [
        ToolbarComponent,
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        ImageUploadComponent
    ],
    providers: [
        DigitalProductService,
        ToastrService
    ]
})
export class DigitalProductDetailsComponent implements OnInit {
    private digitalProductService = inject(DigitalProductService);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private store = inject(Store);

    digitalProductForm!: FormGroup;
    digitalProduct$!: Observable<IDigitalProduct>;
    id!: string | null;
    edit = false;
    imageUrl: string | undefined;
    currentImageFile?: string;

    billingTypes: { value: IDigitalProductBillingType, label: string }[] = [];
    recurringIntervals: { value: IDigitalProductRecurringInterval, label: string }[] = [];
    visibilities: { value: IDigitalProductVisibility, label: string }[] = [];
    private readonly billingTypeMap: Record<string, IDigitalProductBillingType> = {
        '1': IDigitalProductBillingType.OneTime,
        'OneTime': IDigitalProductBillingType.OneTime,
        '2': IDigitalProductBillingType.Recurring,
        'Recurring': IDigitalProductBillingType.Recurring
    };
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        this.edit = this.id !== undefined && this.id !== null && this.id !== '';
        this.imageUrl = undefined;
        this.currentImageFile = undefined;

        this.initializeForm();
        this.initializeSelectOptions();
        this.applyBillingTypeValidators(Number(this.digitalProductForm.controls['billingType'].value) as IDigitalProductBillingType);
        this.watchBillingTypeChanges();
        this.loadDigitalProductData();
    }

    initializeForm(): void {
        this.digitalProductForm = new FormGroup({
            id: new FormControl(''),
            name: new FormControl('', [Validators.required, Validators.maxLength(150)]),
            description: new FormControl('', [Validators.maxLength(4000)]),
            coverUrl: new FormControl('', [Validators.maxLength(500)]),
            currency: new FormControl('USD', [Validators.required, Validators.maxLength(4)]),
            billingType: new FormControl(IDigitalProductBillingType.OneTime, Validators.required),
            recurringInterval: new FormControl(IDigitalProductRecurringInterval.None),
            recurringIntervalCount: new FormControl(null),
            trialDays: new FormControl(null),
            oneTimeFee: new FormControl(null),
            recurringFee: new FormControl(null),
            visibility: new FormControl(IDigitalProductVisibility.Draft, Validators.required)
        });
    }

    initializeSelectOptions(): void {
        this.billingTypes = Object.entries(IDigitalProductBillingType)
            .filter(([key, value]) => typeof value === 'number')
            .filter(([, value]) => value === IDigitalProductBillingType.OneTime)
            .map(([key, value]) => ({ value: value as IDigitalProductBillingType, label: `digitalProduct.billingType.${key}` }));

        this.recurringIntervals = Object.entries(IDigitalProductRecurringInterval)
            .filter(([key, value]) => typeof value === 'number')
            .map(([key, value]) => ({ value: value as IDigitalProductRecurringInterval, label: `digitalProduct.recurringInterval.${key}` }));

        this.visibilities = Object.entries(IDigitalProductVisibility)
            .filter(([key, value]) => typeof value === 'number')
            .map(([key, value]) => ({ value: value as IDigitalProductVisibility, label: `digitalProduct.visibility.${key}` }));
    }

    watchBillingTypeChanges(): void {
        this.digitalProductForm.controls['billingType'].valueChanges.subscribe((billingType: unknown) => {
            this.applyBillingTypeValidators(this.resolveBillingType(billingType));
        });
    }

    applyBillingTypeValidators(billingType: IDigitalProductBillingType): void {
        this.digitalProductForm.controls['billingType'].setValue(IDigitalProductBillingType.OneTime, { emitEvent: false });
        billingType = IDigitalProductBillingType.OneTime;

        const recurringInterval = this.digitalProductForm.controls['recurringInterval'];
        const recurringIntervalCount = this.digitalProductForm.controls['recurringIntervalCount'];
        const trialDays = this.digitalProductForm.controls['trialDays'];
        const recurringFee = this.digitalProductForm.controls['recurringFee'];
        const oneTimeFee = this.digitalProductForm.controls['oneTimeFee'];

        recurringInterval.clearValidators();
        recurringIntervalCount.clearValidators();
        trialDays.clearValidators();
        recurringFee.clearValidators();
        oneTimeFee.clearValidators();

        oneTimeFee.setValidators([Validators.required, Validators.min(0.01)]);

        recurringInterval.setValue(IDigitalProductRecurringInterval.None, { emitEvent: false });
        recurringIntervalCount.setValue(null, { emitEvent: false });
        trialDays.setValue(null, { emitEvent: false });
        recurringFee.setValue(null, { emitEvent: false });

        recurringInterval.updateValueAndValidity({ emitEvent: false });
        recurringIntervalCount.updateValueAndValidity({ emitEvent: false });
        trialDays.updateValueAndValidity({ emitEvent: false });
        recurringFee.updateValueAndValidity({ emitEvent: false });
        oneTimeFee.updateValueAndValidity({ emitEvent: false });
    }

    loadDigitalProductData(): void {
        if (!this.edit || !this.id) {
            return;
        }

        this.store.dispatch(digitalProductActions.getDigitalProduct(this.id));
        this.digitalProduct$ = this.store.select(getCurrentDigitalProductSelector);
        this.digitalProduct$.subscribe(digitalProduct => {
            if (!digitalProduct || !digitalProduct.id || digitalProduct.id !== this.id) {
                return;
            }

            this.digitalProductForm.patchValue({
                id: digitalProduct.id ?? '',
                name: digitalProduct.name ?? '',
                description: digitalProduct.description ?? '',
                coverUrl: digitalProduct.coverUrl ?? '',
                currency: digitalProduct.currency ?? 'USD',
                billingType: IDigitalProductBillingType.OneTime,
                recurringInterval: IDigitalProductRecurringInterval.None,
                recurringIntervalCount: null,
                trialDays: null,
                oneTimeFee: digitalProduct.oneTimeFee ?? null,
                recurringFee: null,
                visibility: this.resolveVisibility(digitalProduct.visibility)
            }, { emitEvent: false });

            this.currentImageFile = digitalProduct.coverUrl ?? undefined;
            this.imageUrl = this.resolveCoverImageUrl(this.currentImageFile);
            this.applyBillingTypeValidators(IDigitalProductBillingType.OneTime);
        });
    }

    isRecurring(): boolean {
        return false;
    }

    deleteCover(shouldDelete?: boolean): void {
        if (shouldDelete !== true) {
            return;
        }

        this.currentImageFile = undefined;
        this.imageUrl = undefined;
        this.digitalProductForm.controls['coverUrl'].setValue(null, { emitEvent: false });
    }

    coverChanged(file: File | null): void {
        this.digitalProductService.changeCover(file, this.id ?? '').subscribe((image) => {
            this.currentImageFile = image ?? undefined;
            this.imageUrl = this.resolveCoverImageUrl(image);
            this.digitalProductForm.controls['coverUrl'].setValue(image ?? null, { emitEvent: false });
        });
    }

    changeDigitalProduct(formValues: IDigitalProduct): void {
        if (this.digitalProductForm.invalid) {
            this.digitalProductForm.markAllAsTouched();
            return;
        }

        const digitalProduct = this.buildDigitalProductPayload(formValues);
        let request$: Observable<IDigitalProduct>;

        if (this.edit && this.id) {
            digitalProduct.id = this.id;
            request$ = this.digitalProductService.editDigitalProduct(digitalProduct);
        } else {
            request$ = this.digitalProductService.createDigitalProduct(digitalProduct);
        }

        request$.subscribe(savedDigitalProduct => {
            if (savedDigitalProduct?.id) {
                const message = this.edit ? 'digitalProduct.update.successMessage' : 'digitalProduct.create.successMessage';
                this.toastService.show(this.translate.instant(message), '');
                this.store.dispatch(digitalProductActions.getDigitalProducts());
                this.router.navigate(['listDigitalProducts']);
            }
        });
    }

    private buildDigitalProductPayload(formValues: IDigitalProduct): IDigitalProduct {
        return {
            ...formValues,
            name: (formValues.name ?? '').trim(),
            description: formValues.description?.trim() ?? null,
            coverUrl: this.currentImageFile?.trim() ?? null,
            currency: (formValues.currency ?? 'USD').trim().toUpperCase(),
            billingType: IDigitalProductBillingType.OneTime,
            recurringInterval: IDigitalProductRecurringInterval.None,
            recurringIntervalCount: null,
            trialDays: null,
            oneTimeFee: this.toNullableNumber(formValues.oneTimeFee),
            recurringFee: null,
            visibility: this.resolveVisibility(formValues.visibility)
        };
    }

    private resolveBillingType(value: unknown): IDigitalProductBillingType {
        return this.resolveEnumValue(value, this.billingTypeMap) ?? IDigitalProductBillingType.OneTime;
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval {
        return this.resolveEnumValue(value, this.recurringIntervalMap) ?? IDigitalProductRecurringInterval.None;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility {
        return this.resolveEnumValue(value, this.visibilityMap) ?? IDigitalProductVisibility.Draft;
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === null || value === undefined) {
            return undefined;
        }

        return map[String(value).trim()];
    }

    private toNullableNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    private resolveCoverImageUrl(coverUrl?: string | null): string | undefined {
        const normalizedCoverUrl = coverUrl?.trim();
        if (!normalizedCoverUrl) {
            return undefined;
        }

        if (normalizedCoverUrl.startsWith('http://') ||
            normalizedCoverUrl.startsWith('https://') ||
            normalizedCoverUrl.startsWith('data:') ||
            normalizedCoverUrl.startsWith('blob:')) {
            return normalizedCoverUrl;
        }

        const sanitizedCoverUrl = normalizedCoverUrl.replace(/\\/g, '/');

        if (sanitizedCoverUrl.startsWith('/products/images/')) {
            return `${environment.API_URL}${sanitizedCoverUrl}`;
        }

        // Backward compatibility for old values saved as /posts/images/{file}
        const fileName = sanitizedCoverUrl.split('/').filter(Boolean).pop();
        if (!fileName) {
            return undefined;
        }

        return `${environment.API_URL}/products/images/${fileName}`;
    }
}
